import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import { Alert, Box, Typography, useMediaQuery } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import {
	useDeleteInspectionMutation,
	useUpsertInspectionMutation,
} from "../hooks/mutations/useInspectionMutations";
import { useSectionSpecAtTime } from "@/shared/hooks/useSectionSpec";
import { useInspectionsQuery } from "../hooks/queries/useInspectionsQuery";
import { useRollingSummaryQuery } from "../hooks/queries/useRollingSummaryQuery";
import {
	createFormDraft,
	reconcileFormDraft,
	type InspectionFormDraft,
} from "./utils/inspectionDraft";
import { validateDraftForSave } from "./utils/inspectionValidation";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	getCurrentSection,
	useSections,
} from "@/shared/selectors/SectionsOfPartSelector";
import type {
	InspectionWithValues,
	UpsertInspectionPayload,
} from "../models/Inspection";
import {
	Group,
	Panel,
	type Layout,
	useDefaultLayout,
	useGroupRef,
} from "react-resizable-panels";

import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useProfileItemsQuery } from "@/pages/configuration/hooks/queries/useProfileItemsQuery";

import { M1InspectionForm } from "./components/M1InspectionForm";
import { References } from "./components/References";
import { Summary, SUMMARY_GRID_HEIGHT } from "./components/Summary";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	HISTORICAL_SAMPLE_DIALOG_TITLE,
	HISTORICAL_SAMPLE_WARNING_MESSAGE,
} from "../shared/historicalSample";

import { useInspectionByIdQuery } from "../hooks/queries/useInspectionByIdQuery";
import { useInspectionUrl } from "../hooks/useInspectionUrl";
import { resolveSampleTime } from "../utils/sampleTime";

const MIN_SUMMARY_PANEL_HEIGHT = SUMMARY_GRID_HEIGHT;
const WORKSPACE_GROUP_ID = "m1-test-shack-workspace";
const REFERENCES_PANEL_ID = "m1-test-shack-references";
const FORM_PANEL_ID = "m1-test-shack-form";
const DESKTOP_PANEL_IDS = [FORM_PANEL_ID, REFERENCES_PANEL_ID];
// Three compact tables need 720px of content plus the form's padding.
// Bound desktop dragging instead of letting a stacked form push Summary off-screen.
const MIN_FORM_PANEL_SIZE = "744px";
// Keep insertion order aligned with the rendered panels: imperative setLayout
// uses this order for subsequent drag calculations.
const DEFAULT_DESKTOP_LAYOUT: Layout = {
	[FORM_PANEL_ID]: 60,
	[REFERENCES_PANEL_ID]: 40,
};

export const M1TestShackPage = ({ assetId }: { assetId: number }) => {
	return <M1TestShackSession key={assetId} assetId={assetId} />;
};

const M1TestShackSession = ({ assetId }: { assetId: number }) => {
	const [inspectionUrl, setInspectionUrl] = useInspectionUrl();
	const inspectionById = useInspectionByIdQuery(assetId);
	const [error, setError] = useState("");
	const onError = (error: Error) => {
		setError(error.message);
	};
	const sections = useSections({ assetId, refetchInterval: 60_000, onError });
	const currentSectionId = getCurrentSection(sections.data)?.SectionId ?? null;
	const resolvedInitialSection = useRef(false);
	const [editing, setEditing] = useState<InspectionWithValues | null>(null);
	// Keep RHF above responsive branches so controller unmounts do not discard edits.
	const formMethods = useForm<InspectionFormDraft>({
		defaultValues: createFormDraft(null),
		shouldUnregister: false,
	});
	const { reset } = formMethods;
	const sectionId = useWatch({
		control: formMethods.control,
		name: "sectionId",
	});
	useEffect(() => {
		if (inspectionUrl !== null) return;
		setEditing(null);
		resolvedInitialSection.current = false;
		reset(createFormDraft(null, formMethods.getValues("sectionId")));
	}, [inspectionUrl, reset, formMethods]);
	useEffect(() => {
		// Restore on entry or ID changes, not on same-ID background refreshes.
		if (
			!inspectionById.data ||
			editing?.ProfileInspectionId === inspectionById.data.ProfileInspectionId
		)
			return;
		resolvedInitialSection.current = true;
		setEditing(inspectionById.data);
		reset(createFormDraft(inspectionById.data));
	}, [inspectionById.data, editing?.ProfileInspectionId, reset]);
	const urlBlocked =
		inspectionById.requested &&
		editing?.ProfileInspectionId !== Number(inspectionUrl);
	const [toDelete, setToDelete] = useState<InspectionWithValues | null>(null);
	const [pendingHistoricalSave, setPendingHistoricalSave] =
		useState<UpsertInspectionPayload | null>(null);
	// One synchronous operation guard: false = idle, inspection = confirming,
	// true = mutating. Consuming the inspection prevents duplicate accepts.
	const savingRef = useRef<boolean | InspectionWithValues>(false);
	const selectInspection = useCallback(
		(inspection: InspectionWithValues) => {
			if (savingRef.current) return;
			void setInspectionUrl(String(inspection.ProfileInspectionId));
		},
		[setInspectionUrl],
	);
	const confirmDeleteInspection = useCallback(
		(inspection: InspectionWithValues) => {
			if (savingRef.current) return;
			savingRef.current = inspection;
			setToDelete(inspection);
		},
		[],
	);
	const saveMutation = useUpsertInspectionMutation({
		onSuccess: () => {
			void setInspectionUrl(null);
			setEditing(null);
			reset(createFormDraft(null, formMethods.getValues("sectionId")));
		},
		onError,
	});

	const deleteMutation = useDeleteInspectionMutation({
		onSuccess: (profileInspectionId) => {
			if (editing?.ProfileInspectionId === profileInspectionId) {
				void setInspectionUrl(null);
				setEditing(null);
				reset(createFormDraft(null, formMethods.getValues("sectionId")));
			}
		},
		onError,
	});
	const closeDelete = async (value?: string) => {
		if (!toDelete || savingRef.current !== toDelete) return;
		setToDelete(null);
		if (value !== "OK") {
			savingRef.current = false;
			return;
		}
		savingRef.current = true;
		try {
			await deleteMutation.mutateAsync(toDelete.ProfileInspectionId);
		} catch {
			// onError reports the failure without discarding the draft or selection.
		} finally {
			savingRef.current = false;
		}
	};

	useEffect(() => {
		if (
			inspectionById.requested ||
			savingRef.current ||
			resolvedInitialSection.current ||
			currentSectionId === null
		)
			return;
		resolvedInitialSection.current = true;
		reset(createFormDraft(null, currentSectionId));
	}, [currentSectionId, reset, inspectionById.requested]);

	const inspectedAt = useWatch({
		control: formMethods.control,
		name: "inspectedAt",
	});
	const selectedSectionId = sectionId;
	const specQuery = useSectionSpecAtTime(
		selectedSectionId,
		assetId,
		inspectedAt,
	);
	const inspectionsQuery = useInspectionsQuery({ assetId, onError });
	const profileItemsQuery = useProfileItemsQuery(assetId);
	const rollingQuery = useRollingSummaryQuery({
		assetId,
		sectionId: currentSectionId,
		onError,
	});
	const lookupReady =
		specQuery.isSuccess &&
		!specQuery.isPreviousData &&
		!specQuery.isFetching &&
		specQuery.isInspectionTimeSettled;
	// The hook retains only same-section data while another sample time resolves.
	const spec = specQuery.data?.spec ?? null;
	const items = spec ? (specQuery.data?.items ?? []) : [];
	const effectiveId = spec?.ProfileSpecId ?? null;
	const [resolved, setResolved] = useState({
		editing,
		id: editing?.ProfileSpecId ?? null,
	});
	const sessionChanged = resolved.editing !== editing;
	const revisionChanged = lookupReady && resolved.id !== effectiveId;
	const canSave =
		inspectionsQuery.isSuccess &&
		!inspectionsQuery.isFetching &&
		!!inspectionsQuery.data?.currentShift;
	const { setValue } = formMethods;

	useEffect(() => {
		// reset(createFormDraft(snapshot)) owns edit restoration. Do not reconcile
		// the previous session's query against the newly restored draft.
		if (sessionChanged) {
			setResolved({ editing, id: editing?.ProfileSpecId ?? null });
			return;
		}
		if (!lookupReady || !revisionChanged) return;
		const next = reconcileFormDraft(formMethods.getValues(), items);
		setValue("readings", next.readings, { shouldDirty: true });
		setValue("angles", next.angles, { shouldDirty: true });
		setResolved({ editing, id: effectiveId });
	}, [
		editing,
		effectiveId,
		lookupReady,
		revisionChanged,
		sessionChanged,
		setValue,
		formMethods,
		items,
	]);

	const ready =
		lookupReady &&
		!!spec &&
		spec.SectionId === selectedSectionId &&
		!sessionChanged &&
		!revisionChanged;
	const save = async (draft: InspectionFormDraft) => {
		if (urlBlocked || savingRef.current) return;
		if (editing && !formMethods.formState.isDirty) return;
		if (!canSave) {
			formMethods.setError("root.context", {
				message:
					"Could not verify the current shift. Refresh inspections before saving.",
			});
			return;
		}
		if (
			!ready ||
			!spec ||
			selectedSectionId === null ||
			spec.SectionId !== selectedSectionId ||
			!draft.inspectedAt ||
			draft.inspectedAt.getTime() !== inspectedAt?.getTime()
		) {
			formMethods.setError("root.context", {
				message:
					"Select a section with an available specification before saving.",
			});
			return;
		}
		const weightProfileItemId = specQuery.data?.weightProfileItemId ?? null;
		const validation = validateDraftForSave(draft, items, weightProfileItemId);
		if (!validation.ok) {
			formMethods.setError(validation.field, { message: validation.message });
			return;
		}
		const payload: UpsertInspectionPayload = {
			profileInspectionId: editing?.ProfileInspectionId ?? null,
			assetId,
			sectionId: selectedSectionId,
			heatId: validation.heatId,
			heatName: null,
			inspectedAt: resolveSampleTime(
				new Date(draft.inspectedAt.getTime()),
				editing !== null || formMethods.getFieldState("inspectedAt").isDirty,
			),
			measuredLbsPerFoot: validation.weight,
			weightProfileItemId,
			comments: draft.comments.trim() || null,
			readings: validation.readings,
		};
		savingRef.current = true;
		const currentShift = inspectionsQuery.data?.currentShift;
		if (
			currentShift &&
			payload.inspectedAt.getTime() < currentShift.Start.getTime()
		) {
			// Keep the lock until the user answers the historical sample warning.
			setPendingHistoricalSave(payload);
			return;
		}
		await submitPayload(payload);
	};
	const submitPayload = async (payload: UpsertInspectionPayload) => {
		try {
			await saveMutation.mutateAsync(payload);
		} catch {
			// onError displays the server error; retain the current draft and edit selection.
		} finally {
			savingRef.current = false;
		}
	};
	const closeHistoricalSave = (value?: string) => {
		const payload = pendingHistoricalSave;
		setPendingHistoricalSave(null);
		if (value === "OK" && payload) {
			void submitPayload(payload);
			return;
		}
		savingRef.current = false;
	};
	const cancelEdit = () => {
		if (savingRef.current) return;
		void setInspectionUrl(null);
		setEditing(null);
		reset(createFormDraft(null, sectionId));
	};
	const saving =
		toDelete !== null ||
		pendingHistoricalSave !== null ||
		deleteMutation.isPending ||
		saveMutation.isPending ||
		formMethods.formState.isSubmitting;
	const form = (
		<>
			{urlBlocked && inspectionById.error && (
				<Alert severity="error">{inspectionById.error}</Alert>
			)}
			<M1InspectionForm
				assetId={assetId}
				form={formMethods}
				sectionId={selectedSectionId}
				items={items}
				editing={editing}
				spec={spec}
				weightProfileItemId={specQuery.data?.weightProfileItemId ?? null}
				// Saving before the spec loads would validate against no dimensions.
				canSave={canSave && ready && !sections.isInitialLoading && !urlBlocked}
				specError={specQuery.error?.message}
				loading={
					urlBlocked ||
					sections.isInitialLoading ||
					specQuery.isFetching ||
					!specQuery.isInspectionTimeSettled
				}
				hasSpec={!!spec}
				specMissing={
					!urlBlocked && selectedSectionId !== null && lookupReady && !spec
				}
				saving={saving || urlBlocked}
				onSubmit={formMethods.handleSubmit(save)}
				onSectionChange={(id) => {
					if (savingRef.current) return;
					resolvedInitialSection.current = true;
					if (id === sectionId) return;
					if (editing) return;
					// Keep a time the user picked; otherwise start from the current minute.
					const { isDirty } = formMethods.getFieldState("inspectedAt");
					const inspectedAt = formMethods.getValues("inspectedAt");
					reset(createFormDraft(null, id));
					if (isDirty)
						formMethods.setValue("inspectedAt", inspectedAt, {
							shouldDirty: true,
						});
				}}
				onCancel={cancelEdit}
			/>
		</>
	);
	const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
	// Allow 744px for the form within its 60%, plus page padding and separator.
	const isWideDesktop = useMediaQuery("(min-width: 1300px)");
	const groupRef = useGroupRef();
	const { defaultLayout, onLayoutChanged } = useDefaultLayout({
		id: WORKSPACE_GROUP_ID,
		panelIds: DESKTOP_PANEL_IDS,
	});

	return (
		<>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ConfirmationDialogRaw
				data-test-id="historical-inspection-dialog"
				open={pendingHistoricalSave !== null}
				title={HISTORICAL_SAMPLE_DIALOG_TITLE}
				message={HISTORICAL_SAMPLE_WARNING_MESSAGE}
				buttons={{ no: "Review date", yes: "Save anyway" }}
				onClose={closeHistoricalSave}
			/>
			<ConfirmationDialogRaw
				data-test-id="delete-inspection-dialog"
				open={toDelete !== null}
				title="Delete inspection"
				message={
					toDelete
						? `Delete the sample of heat ${toDelete.HeatName ?? "-"} taken on ${dxtToLocalServerTime(toDelete.InspectedAt, "dd MMM yyyy HH:mm")}?`
						: ""
				}
				buttons={{ no: "Cancel", yes: "Delete" }}
				onClose={closeDelete}
			/>
			<Box
				sx={{
					minHeight: "calc(100vh - 64px)",
					height: { xs: "auto", md: "calc(100vh - 64px)" },
					boxSizing: "border-box",
					px: 2,
					py: 2,
					display: "grid",
					gridTemplateRows: "auto minmax(min-content, 1fr)",
					gap: 1,
				}}
			>
				<Typography variant="h5" component="h1" fontWeight={600}>
					Section Profile - Test Shack
				</Typography>

				<Box
					sx={{
						display: "grid",
						gridTemplateRows: {
							xs: "auto auto",
							md: "minmax(min-content, 1fr) auto",
						},
						gap: 1,
						minHeight: 0,
					}}
				>
					<Box
						sx={{
							minHeight: 0,
							overflow: "visible",
						}}
					>
						{isDesktop ? (
							<Group
								id={WORKSPACE_GROUP_ID}
								groupRef={groupRef}
								orientation="horizontal"
								defaultLayout={defaultLayout ?? DEFAULT_DESKTOP_LAYOUT}
								onLayoutChanged={onLayoutChanged}
								style={{ height: "100%", overflow: "visible" }}
							>
								<Panel
									id={FORM_PANEL_ID}
									minSize={isWideDesktop ? MIN_FORM_PANEL_SIZE : "25%"}
									style={{ overflow: "visible" }}
								>
									{form}
								</Panel>
								<CustomSeparator
									onDoubleClick={() =>
										groupRef.current?.setLayout(DEFAULT_DESKTOP_LAYOUT)
									}
								/>
								<Panel
									id={REFERENCES_PANEL_ID}
									minSize="40%"
									style={{ overflow: "visible" }}
								>
									<References sectionId={selectedSectionId} spec={spec} />
								</Panel>
							</Group>
						) : (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "minmax(0, 1fr)",
									gap: 1,
									alignItems: "start",
								}}
							>
								{form}
								<References sectionId={selectedSectionId} spec={spec} />
							</Box>
						)}
					</Box>
					<Box sx={{ display: "flex", minHeight: MIN_SUMMARY_PANEL_HEIGHT }}>
						<Summary
							profileItems={profileItemsQuery.data}
							inspections={inspectionsQuery.data?.inspections}
							applicableItemsBySpec={
								inspectionsQuery.data?.applicableItemsBySpec
							}
							rolling={rollingQuery.data}
							averageSectionLabel={
								getCurrentSection(sections.data)?.SectionName
							}
							error={
								inspectionsQuery.error?.message ??
								profileItemsQuery.error?.message
							}
							rollingError={
								rollingQuery.error?.message || sections.error?.message
							}
							selectDisabled={saving}
							onDelete={confirmDeleteInspection}
							onSelect={selectInspection}
						/>
					</Box>
				</Box>
			</Box>
		</>
	);
};
